import { Component, Input, OnInit, OnDestroy, Output, EventEmitter } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { EquipmentPointModel } from "../../../models/equipment-point.model";
import { InspectionModel } from "../../../models/inspection.model";
import { InspectionService } from "../services/inspection.service";
import { NotificationService } from "../../shared/services/notification.service";
import { LoaderService } from "../../shared/services/loader.service";
import { Subscription } from 'rxjs';
import { finalize } from 'rxjs/operators';

@Component({
    selector: 'app-inspection-results-detail',
    templateUrl: './inspection-results-detail.component.html',
    styleUrls: ['./inspection-results-detail.component.scss']
})
export class InspectionResultsDetailComponent implements OnInit, OnDestroy {
    @Input() point!: EquipmentPointModel;
    @Output() forcedCRUpdated = new EventEmitter<void>();

    displayedInspections: InspectionModel[] = [];
    forcedCRValues: { [key: string]: number | null } = {};

    currentPageIndex = 0;
    pageSize = 4;
    totalItems = 0;
    totalPages = 0;

    private subscriptions: Subscription = new Subscription();

    constructor(
        private inspectionService: InspectionService,
        private notificationService: NotificationService,
        private translateService: TranslateService,
        private loaderService: LoaderService
    ) {}

    ngOnInit(): void {
        this.loadInspections();
    }

    ngOnDestroy(): void {
        this.subscriptions.unsubscribe();
    }

    loadInspections(): void {
        if (!this.point || !this.point.id) {
            return;
        }

        this.loaderService.showLoader();

        const subscription = this.inspectionService.getCompletedInspectionsByPoint(
            this.point.id,
            this.currentPageIndex,
            this.pageSize
        ).pipe(
            finalize(() => this.loaderService.hideLoader())
        ).subscribe({
            next: (data) => {
                // Inversez l'ordre pour que les plus récentes soient à droite
                this.displayedInspections = [...data.inspections].reverse();
                this.totalItems = data.total;
                this.totalPages = Math.ceil(this.totalItems / this.pageSize);

                // Initialiser les valeurs forcedCR à partir des données
                this.displayedInspections.forEach(inspection => {
                    if (inspection.result) {
                        this.forcedCRValues[inspection.id] = inspection.result.forcedCorrosionRate || null;
                    }
                });
            },
            error: (error) => {
                console.error('Error loading inspections:', error);
                this.notificationService.error(
                    this.translateService.instant('pages.inspectionResultsDetail.loadError')
                );
            }
        });

        this.subscriptions.add(subscription);
    }

    // Navigation vers les inspections plus anciennes
    nextPage(): void {
        if (this.currentPageIndex < this.totalPages - 1) {
            this.currentPageIndex++;
            this.loadInspections();
        }
    }

    // Navigation vers les inspections plus récentes
    previousPage(): void {
        if (this.currentPageIndex > 0) {
            this.currentPageIndex--;
            this.loadInspections();
        }
    }

    updateForcedCR(inspection: InspectionModel, value: number | null): void {
        value = value ?? 0;
        this.forcedCRValues[inspection.id] = value;
    }

    saveForcedCR(): void {
        const updatePromises = Object.keys(this.forcedCRValues)
            .filter(id => this.forcedCRValues[id] !== null)
            .map(id => {
                if (this.forcedCRValues[id] !== null) {
                    return this.inspectionService.updateInspectionForcedCR(id, this.forcedCRValues[id] as number)
                        .toPromise()
                        .catch(err => {
                            console.error(`Error updating forced CR for inspection ${id}:`, err);
                            throw err;
                        });
                }
                return Promise.resolve();
            });

        this.loaderService.showLoader();

        Promise.all(updatePromises)
            .then(() => {
                this.notificationService.success(
                    this.translateService.instant('pages.inspectionResultsDetail.forcedCRUpdateSuccess')
                );
                // Recharger les données pour obtenir les valeurs mises à jour
                this.loadInspections();
                // Émettre l'événement pour notifier le parent que forcedCR a été mis à jour
                this.forcedCRUpdated.emit();
            })
            .catch(() => {
                this.notificationService.error(
                    this.translateService.instant('pages.inspectionResultsDetail.forcedCRUpdateError')
                );
            })
            .finally(() => {
                this.loaderService.hideLoader();
            });
    }

    // Obtenir la classe CSS basée sur le code de recommandation
    getRecommendationClass(code: number | undefined): string {
        switch (code) {
            case 1:
                return 'recommendation-code-1'; // Do RBI - Light green
            case 2:
                return 'recommendation-code-2'; // Consider further inspection - Yellow
            case 3:
                return 'recommendation-code-3'; // Do further inspection - Orange
            case 4:
                return 'recommendation-code-4'; // Immediate full inspection - Red
            case 5:
                return 'recommendation-code-5'; // Input review
            default:
                return '';
        }
    }

    get paginationText(): string {
        if (this.totalItems === 0) return this.translateService.instant('common.pagination.empty');

        const startItem = this.totalItems - (this.currentPageIndex * this.pageSize) - this.displayedInspections.length + 1;
        const endItem = this.totalItems - (this.currentPageIndex * this.pageSize);

        return `${startItem} - ${endItem} ${this.translateService.instant('common.pagination.of')} ${this.totalItems}`;
    }
}
